import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ChevronDown, ChevronUp, HelpCircle, Lock, AlertTriangle, MessageSquare, Info } from "lucide-react";
import { renderMergeFields, type ContractData, buildMergeContext } from "@/lib/mergeFields";
import { sanitizeHtml } from "@/lib/sanitize";
import type { ContractClauseOverride } from "./AttorneyClauseView";

const CATEGORY_LABELS: Record<string, string> = {
  compensation: "Compensation",
  termination: "Termination",
  buyout: "Buyout",
  nil: "NIL",
  title_ix: "Title IX",
  indemnification: "Indemnification",
  governing_law: "Governing Law",
  outside_income: "Outside Income",
  general: "General",
};

const CATEGORY_COLORS: Record<string, string> = {
  compensation: "bg-emerald-50 text-emerald-700 border-emerald-200",
  termination: "bg-red-50 text-red-700 border-red-200",
  buyout: "bg-orange-50 text-orange-700 border-orange-200",
  nil: "bg-purple-50 text-purple-700 border-purple-200",
  title_ix: "bg-blue-50 text-blue-700 border-blue-200",
  indemnification: "bg-slate-50 text-slate-700 border-slate-200",
  governing_law: "bg-gray-50 text-gray-700 border-gray-200",
  outside_income: "bg-yellow-50 text-yellow-700 border-yellow-200",
  general: "bg-muted text-muted-foreground border-border",
};

function plainLanguageSummary(clauseTitle: string, category: string, contractData: ContractData): string {
  const salary = contractData.annualSalary != null ? `$${contractData.annualSalary.toLocaleString()}` : "the agreed salary";
  const buyout = contractData.buyoutClause != null ? `$${contractData.buyoutClause.toLocaleString()}` : "the buyout amount";
  const startDate = contractData.startDate ? new Date(contractData.startDate).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "the start date";
  const endDate = contractData.endDate ? new Date(contractData.endDate).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "the end date";

  switch (category) {
    case "compensation":
      return `This clause covers how ${contractData.coachName ?? "the coach"} will be paid. The base annual salary is ${salary}.`;
    case "termination":
      return `This clause explains what happens if the contract ends early — either by the university or by the coach — and what obligations each party has.`;
    case "buyout":
      return `If the university terminates the coach without cause, a buyout payment of ${buyout} may be owed. This clause defines how and when that is calculated.`;
    case "nil":
      return `This clause covers rights related to the coach's name, image, and likeness (NIL) — including any restrictions or permissions.`;
    case "title_ix":
      return `This clause includes the university's obligations under Title IX, ensuring non-discrimination in athletic programs.`;
    case "indemnification":
      return `This clause protects both parties from certain legal liabilities and explains who is responsible for covering legal costs in defined situations.`;
    case "governing_law":
      return `This clause specifies which state's laws govern this contract and where any disputes would be resolved.`;
    case "outside_income":
      return `This clause governs any income the coach earns from outside activities like speaking engagements, endorsements, or media appearances.`;
    default:
      return `This clause (${clauseTitle}) sets out specific terms that apply to this contract, running from ${startDate} to ${endDate}.`;
  }
}

function tooltipText(category: string): string {
  switch (category) {
    case "compensation": return "Defines how and when the coach receives their salary, including payment frequency and any automatic increases.";
    case "termination": return "Covers the conditions under which either party can end the agreement early, and what happens if they do.";
    case "buyout": return "A buyout is a lump-sum payment the university makes to a coach when terminating their contract without cause — essentially buying out the remaining term.";
    case "nil": return "NIL (Name, Image, and Likeness) rights determine whether and how the coach can profit from their personal brand during the contract period.";
    case "title_ix": return "Title IX is a federal law prohibiting sex discrimination in educational programs that receive federal funding, including athletics.";
    case "indemnification": return "Indemnification means one party agrees to compensate the other for certain losses or legal expenses. This clause outlines who is protected and under what circumstances.";
    case "governing_law": return "If a dispute arises, this clause specifies which state's courts and laws will apply — important for determining where a lawsuit could be filed.";
    case "outside_income": return "Coaches often earn money from camps, endorsements, or media. This clause sets rules about what income must be disclosed or restricted during the contract.";
    default: return "This is a general contract provision that sets out specific rights or obligations of one or both parties.";
  }
}

type InclusionCondition = { kind: "always" } | { kind: "never" } | { kind: "conditional"; description?: string } | Record<string, unknown>;

interface ContractClauseItem {
  id: number;
  contractId: number;
  position: number;
  clause: {
    id: number;
    title: string;
    category: string;
    jurisdiction: string | null;
    tags: string[];
    inclusionCondition?: InclusionCondition | null;
    inclusionRequired?: boolean;
    lastReviewedAt?: string | null;
    approvingAttorney?: string | null;
  };
  version: {
    id: number;
    versionNumber: number;
    body: string;
  };
}

function inclusionLabel(cond: InclusionCondition | null | undefined): string {
  if (!cond) return "Always included";
  const kind = (cond as { kind?: string }).kind;
  if (kind === "always") return "Always included";
  if (kind === "never") return "Excluded unless manually added";
  if (kind === "conditional") {
    const desc = (cond as { description?: string }).description;
    return desc ? `Conditional: ${desc}` : "Included conditionally based on contract terms";
  }
  return "Included per legal policy";
}

function WhyThisClause({ cc }: { cc: ContractClauseItem }) {
  const c = cc.clause;
  const hasProvenance = c.lastReviewedAt || c.approvingAttorney;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
          title="Why is this clause included?"
        >
          <Info className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-3 space-y-2.5" side="bottom" align="start">
        <div className="text-xs font-semibold text-foreground">Why this clause?</div>
        <div className="space-y-1.5">
          <div>
            <div className="text-xs text-muted-foreground">Inclusion rule</div>
            <div className="text-xs font-medium">{inclusionLabel(c.inclusionCondition)}</div>
          </div>
          {c.inclusionRequired && (
            <div>
              <div className="text-xs text-muted-foreground">Required</div>
              <div className="text-xs font-medium">Yes — this clause is mandatory</div>
            </div>
          )}
          <div>
            <div className="text-xs text-muted-foreground">Library version</div>
            <div className="text-xs font-medium">v{cc.version.versionNumber}</div>
          </div>
          {c.lastReviewedAt && (
            <div>
              <div className="text-xs text-muted-foreground">Last reviewed</div>
              <div className="text-xs font-medium">
                {new Date(c.lastReviewedAt).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
              </div>
            </div>
          )}
          {c.approvingAttorney && (
            <div>
              <div className="text-xs text-muted-foreground">Approving attorney</div>
              <div className="text-xs font-medium">{c.approvingAttorney}</div>
            </div>
          )}
          {!hasProvenance && (
            <div className="text-xs text-muted-foreground italic">No review history on record.</div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

interface NegotiationItemRef {
  id: number;
  clauseRef: string | null;
  fieldRef: string | null;
  status: string;
  summary: string;
  raisedBy: string;
}

function clauseNegotiationItems(clauseTitle: string, items: NegotiationItemRef[]): NegotiationItemRef[] {
  const title = clauseTitle.toLowerCase();
  return items.filter((item) => {
    if (!item.clauseRef) return false;
    const ref = item.clauseRef.toLowerCase();
    return ref.includes(title) || title.includes(ref);
  });
}

function NegotiationIndicator({ items }: { items: NegotiationItemRef[] }) {
  if (items.length === 0) return null;
  const openCount = items.filter((i) => i.status === "open").length;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          className={`shrink-0 flex items-center gap-1 text-xs rounded px-1.5 py-0.5 border font-medium ${openCount > 0 ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-muted text-muted-foreground border-border"}`}
          title={`${items.length} negotiation item${items.length > 1 ? "s" : ""} for this clause`}
        >
          <MessageSquare className="h-3 w-3" />
          {items.length}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-3 space-y-2" side="bottom" align="start">
        <div className="text-xs font-semibold">Negotiation items for this clause</div>
        {items.map((item) => (
          <div key={item.id} className={`text-xs p-2 rounded border space-y-0.5 ${item.status === "open" ? "border-amber-200 bg-amber-50" : "border-border bg-muted/30"}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium">{item.raisedBy}</span>
              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${item.status === "open" ? "bg-amber-100 text-amber-800" : item.status === "accepted" ? "bg-green-100 text-green-800" : item.status === "rejected" ? "bg-red-100 text-red-800" : "bg-slate-100 text-slate-700"}`}>
                {item.status}
              </span>
            </div>
            <div className="text-muted-foreground leading-snug">{item.summary}</div>
          </div>
        ))}
      </PopoverContent>
    </Popover>
  );
}

interface DrafterClauseViewProps {
  clauses: ContractClauseItem[];
  overrides: Map<number, ContractClauseOverride>;
  contractData: ContractData;
  negotiationItems?: NegotiationItemRef[];
  onSwitchToAttorney?: () => void;
}

export function DrafterClauseView({ clauses, overrides, contractData, negotiationItems = [], onSwitchToAttorney }: DrafterClauseViewProps) {
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const mergeContext = buildMergeContext(contractData);

  const toggleExpanded = (id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const lockedClauses = clauses.filter((cc) => overrides.get(cc.id)?.isLocked);
  const alternateClauses = clauses.filter((cc) => overrides.get(cc.id)?.proposedAlternate);

  const hasBanner = lockedClauses.length > 0 || alternateClauses.length > 0;

  const grouped = clauses.reduce<Record<string, ContractClauseItem[]>>((acc, cc) => {
    const key = cc.clause.category;
    if (!acc[key]) acc[key] = [];
    acc[key].push(cc);
    return acc;
  }, {});

  return (
    <TooltipProvider>
      <div className="space-y-4">
        {hasBanner && (
          <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
            <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-amber-800">Legal review in progress</p>
              <p className="text-amber-700 text-xs mt-0.5">
                {lockedClauses.length > 0 && `${lockedClauses.length} clause${lockedClauses.length > 1 ? "s are" : " is"} locked by Legal. `}
                {alternateClauses.length > 0 && `${alternateClauses.length} clause${alternateClauses.length > 1 ? "s have" : " has"} a proposed alternate. `}
                Certain edits may require a Legal review request.
              </p>
            </div>
            {onSwitchToAttorney && (
              <Button variant="outline" size="sm" className="shrink-0 text-xs h-7 border-amber-300 text-amber-800 hover:bg-amber-100" onClick={onSwitchToAttorney}>
                View in Attorney mode
              </Button>
            )}
          </div>
        )}

        {clauses.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">
            No clauses attached to this contract yet.
          </div>
        ) : (
          Object.entries(grouped).map(([cat, items]) => (
            <div key={cat}>
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {CATEGORY_LABELS[cat] ?? cat}
                </h3>
                <span className="text-xs text-muted-foreground">({items.length})</span>
              </div>
              <div className="space-y-2">
                {items.map((cc) => {
                  const override = overrides.get(cc.id);
                  const isLocked = override?.isLocked ?? false;
                  const hasAlternate = !!override?.proposedAlternate;
                  const isExpanded = expandedIds.has(cc.id);
                  const body = override?.body ?? cc.version.body;
                  const renderedBody = renderMergeFields(body, mergeContext);
                  const summary = plainLanguageSummary(cc.clause.title, cc.clause.category, contractData);
                  const tip = tooltipText(cc.clause.category);
                  const clauseNegItems = clauseNegotiationItems(cc.clause.title, negotiationItems);

                  return (
                    <div key={cc.id} className={`border rounded-lg overflow-hidden ${isLocked ? "border-amber-300 bg-amber-50/30" : ""}`}>
                      <div className="flex items-start gap-2 p-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <span className="font-medium text-sm">{cc.clause.title}</span>
                            <Badge variant="outline" className={`text-xs ${CATEGORY_COLORS[cc.clause.category] ?? ""}`}>
                              {CATEGORY_LABELS[cc.clause.category] ?? cc.clause.category}
                            </Badge>
                            <span className="text-xs text-muted-foreground">v{cc.version.versionNumber}</span>
                            <NegotiationIndicator items={clauseNegItems} />
                            {override?.body && (
                              <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                                Edited
                              </Badge>
                            )}
                            {isLocked && (
                              <span className="flex items-center gap-1 text-xs text-amber-700 font-medium">
                                <Lock className="h-3 w-3" /> Locked
                              </span>
                            )}
                            {hasAlternate && (
                              <span className="flex items-center gap-1 text-xs text-blue-700 font-medium">
                                <MessageSquare className="h-3 w-3" /> Alternate proposed
                              </span>
                            )}
                          </div>

                          <div className="flex items-start gap-1.5">
                            <p className="text-sm text-muted-foreground leading-relaxed">{summary}</p>
                            <WhyThisClause cc={cc} />
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button className="shrink-0 mt-0.5 text-muted-foreground hover:text-foreground">
                                  <HelpCircle className="h-3.5 w-3.5" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent className="max-w-[280px]">
                                <p className="text-xs">{tip}</p>
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        </div>

                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleExpanded(cc.id)}
                          className="h-7 px-2 text-xs shrink-0"
                        >
                          {isExpanded ? (
                            <><ChevronUp className="h-3 w-3 mr-1" /> Hide text</>
                          ) : (
                            <><ChevronDown className="h-3 w-3 mr-1" /> Show text</>
                          )}
                        </Button>
                      </div>

                      {isExpanded && (
                        <div
                          className="px-4 pb-4 text-sm leading-relaxed prose prose-sm max-w-none dark:prose-invert border-t pt-3"
                          dangerouslySetInnerHTML={{ __html: sanitizeHtml(renderedBody) }}
                        />
                      )}

                      {isLocked && (
                        <div className="px-3 pb-2 flex items-center gap-1.5 text-xs text-amber-700">
                          <Lock className="h-3 w-3" />
                          <span>This clause has been approved by Legal. Input changes affecting this clause will require a Legal review request.</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>
    </TooltipProvider>
  );
}
